import { Decimal } from '@prisma/client/runtime/library';
import { POStatus } from '@prisma/client';

export interface ReceiveItemInput {
  poItemId: string;
  itemId: string;
  uomId: string;
  receivedQty: number; // Good quality quantity -> increases inventory
  rejectedQty: number; // Damaged / rejected quantity -> quality audit only
  conditionNotes?: string;
  unitCost: number; // For inventory cost layer
}

export interface ValidateGRItemReceiptResult {
  valid: boolean;
  error?: string;
  outstandingQty: number;
}

/**
 * Validates Goods Receipt against PO Item to strictly prevent over-delivery.
 * Directive 3: GR Over-delivery: Strictly forbidden. Received quantity must never exceed outstanding PO quantity.
 * Directive 4: GR Inspection: Goods Receipt records both good received quantity and rejected/damaged quantity.
 * Total delivered by vendor for this receipt item is receivedQty (good) + rejectedQty (rejected at gate),
 * or strictly receivedQty against outstanding orderedQty depending on company policy.
 * Here we validate that total received good items never exceeds outstanding PO quantity.
 */
export function validateGROverDelivery(
  orderedQty: number | Decimal,
  previouslyReceivedQty: number | Decimal,
  newReceivedQty: number
): ValidateGRItemReceiptResult {
  const ordered = Number(orderedQty);
  const previouslyReceived = Number(previouslyReceivedQty);
  const outstandingQty = ordered - previouslyReceived;

  if (newReceivedQty < 0) {
    return {
      valid: false,
      error: 'Received quantity cannot be negative.',
      outstandingQty,
    };
  }

  if (newReceivedQty > outstandingQty) {
    return {
      valid: false,
      error: `GR Over-delivery forbidden: incoming good received quantity (${newReceivedQty}) exceeds outstanding PO quantity (${outstandingQty}). Ordered: ${ordered}, Previously Received: ${previouslyReceived}.`,
      outstandingQty,
    };
  }

  return {
    valid: true,
    outstandingQty: outstandingQty - newReceivedQty,
  };
}

/**
 * Separates receipt quantities into inventory increment and vendor quality audit tracking.
 * Directive 4: Only good received quantity increases inventory.
 */
export function processGRInspection(input: ReceiveItemInput): {
  inventoryIncrementQty: number;
  qualityAuditRejectedQty: number;
  createsCostLayer: boolean;
} {
  return {
    inventoryIncrementQty: input.receivedQty,
    qualityAuditRejectedQty: input.rejectedQty,
    createsCostLayer: input.receivedQty > 0,
  };
}

/**
 * Calculates updated PO status based on received quantities of all line items.
 * If all items are completely received, PO is COMPLETED.
 * If at least one item has receivedQty > 0, PO is PARTIALLY_RECEIVED.
 * Otherwise remains ISSUED.
 */
export function calculatePOReceiptStatus(
  items: Array<{ orderedQty: number | Decimal; receivedQty: number | Decimal }>
): POStatus {
  if (!items || items.length === 0) return POStatus.ISSUED;
  const allFullyReceived = items.every((i) => Number(i.receivedQty) >= Number(i.orderedQty));
  if (allFullyReceived) {
    return POStatus.COMPLETED;
  }
  const anyReceived = items.some((i) => Number(i.receivedQty) > 0);
  if (anyReceived) {
    return POStatus.PARTIALLY_RECEIVED;
  }
  return POStatus.ISSUED;
}

